/**
 * Pure helpers backing the weekly-hours editor. No React here — the
 * component (Task 2) is a thin renderer over these functions so the
 * interaction logic (presets, copy-to-days, bulk apply, validation,
 * summary text) is unit-testable without a DOM.
 *
 * Emits the backend HoursSpec shape: { weekly: { mon: [["09:00","18:00"]], ... } }
 * — or null when empty. Times are zero-padded 24h "HH:MM" strings; compare
 * them as strings (the padding is what makes that ordering-safe).
 */

import type { WeeklyHours } from '@/types/commerce'

export const DAY_KEYS: readonly string[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export const DAY_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

const DEFAULT_INTERVAL: [string, string] = ['09:00', '18:00']

export interface Preset {
  id: string
  label: string
  build: () => WeeklyHours | null
}

function buildFor(days: readonly string[], interval: [string, string]): WeeklyHours | null {
  const weekly: Record<string, [string, string][]> = {}
  for (const day of days) {
    weekly[day] = [[...interval] as [string, string]]
  }
  return normalize({ weekly })
}

const WEEKDAYS = DAY_KEYS.slice(0, 5)

export const PRESETS: readonly Preset[] = [
  {
    id: 'weekdays-9-6',
    label: 'Weekdays 9–6',
    build: () => buildFor(WEEKDAYS, DEFAULT_INTERVAL),
  },
  {
    id: 'everyday-9-6',
    label: 'Every day 9–6',
    build: () => buildFor(DAY_KEYS, DEFAULT_INTERVAL),
  },
  {
    id: 'clear',
    label: 'Clear all',
    build: () => null,
  },
]

/**
 * Drops days whose interval list is empty and returns null when nothing
 * remains. Every other mutating helper routes through this so the
 * "omit empty days, null when all closed" invariant lives in one place.
 */
export function normalize(value: WeeklyHours | null): WeeklyHours | null {
  if (!value) return null
  const weekly: Record<string, [string, string][]> = {}
  for (const day of Object.keys(value.weekly)) {
    const intervals = value.weekly[day]
    if (intervals && intervals.length > 0) {
      weekly[day] = intervals.map((iv) => [iv[0], iv[1]] as [string, string])
    }
  }
  return Object.keys(weekly).length > 0 ? { weekly } : null
}

export function setDayIntervals(
  value: WeeklyHours | null,
  day: string,
  intervals: [string, string][],
): WeeklyHours | null {
  const weekly = { ...(value?.weekly ?? {}) }
  weekly[day] = intervals.map((iv) => [iv[0], iv[1]] as [string, string])
  return normalize({ weekly })
}

export function copyDayTo(
  value: WeeklyHours | null,
  from: string,
  targets: readonly string[],
): WeeklyHours | null {
  const source = value?.weekly[from] ?? []
  const weekly = { ...(value?.weekly ?? {}) }
  for (const target of targets) {
    if (target === from) continue
    weekly[target] = source.map((iv) => [iv[0], iv[1]] as [string, string])
  }
  return normalize({ weekly })
}

export function applyToDays(
  value: WeeklyHours | null,
  days: readonly string[],
  interval: [string, string],
): WeeklyHours | null {
  const weekly = { ...(value?.weekly ?? {}) }
  for (const day of days) {
    weekly[day] = [[interval[0], interval[1]] as [string, string]]
  }
  return normalize({ weekly })
}

export function closeDays(value: WeeklyHours | null, days: readonly string[]): WeeklyHours | null {
  const weekly = { ...(value?.weekly ?? {}) }
  for (const day of days) {
    weekly[day] = []
  }
  return normalize({ weekly })
}

// Mirrors the server's _TIME_RE (backend/app/schemas/business.py) exactly.
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * Mirrors the server's HoursSpec validator (backend/app/schemas/business.py
 * `_validate_weekly`): a single pass over the intervals in the given order,
 * checking the time-format of both endpoints first (same as the server's
 * `_TIME_RE` check, which runs before its ordering checks), then per-interval
 * open < close, then — against the running close of the previous interval —
 * that the next open doesn't land before it. Intervals are expected
 * pre-sorted, same as the server. Advisory only: never gates onChange.
 */
export function validateDay(intervals: [string, string][]): string | null {
  let prevClose: string | null = null
  for (const [open, close] of intervals) {
    if (!TIME_RE.test(open) || !TIME_RE.test(close)) {
      return 'Enter a valid time'
    }
    if (!(open < close)) {
      return 'Opening time must be before closing time'
    }
    if (prevClose !== null && open < prevClose) {
      return 'Time ranges must not overlap'
    }
    prevClose = close
  }
  return null
}

function stripLeadingZero(time: string): string {
  return time.replace(/^0(\d:)/, '$1')
}

function formatInterval(interval: [string, string]): string {
  return `${stripLeadingZero(interval[0])}–${stripLeadingZero(interval[1])}`
}

function formatDay(intervals: [string, string][]): string {
  if (intervals.length === 0) return 'closed'
  return intervals.map(formatInterval).join(', ')
}

/**
 * Renders e.g. "Mon–Fri 9:00–18:00 · Sat 10:00–14:00 · Sun closed".
 * Consecutive days (in DAY_KEYS order) with identical interval lists
 * collapse into a single "First–Last" range; a lone day renders as just
 * its label. summarize(null) returns "Closed all week".
 */
export function summarize(value: WeeklyHours | null): string {
  if (!value) return 'Closed all week'

  const perDay = DAY_KEYS.map((day) => value.weekly[day] ?? [])
  const segments: string[] = []
  let i = 0
  while (i < DAY_KEYS.length) {
    let j = i
    while (
      j + 1 < DAY_KEYS.length &&
      JSON.stringify(perDay[j + 1]) === JSON.stringify(perDay[i])
    ) {
      j++
    }
    const label =
      j === i
        ? DAY_LABELS[DAY_KEYS[i]]
        : `${DAY_LABELS[DAY_KEYS[i]]}–${DAY_LABELS[DAY_KEYS[j]]}`
    segments.push(`${label} ${formatDay(perDay[i])}`)
    i = j + 1
  }

  return segments.join(' · ')
}
